/**
 * FlameGraphCustom - Custom React 18 Flamegraph Component
 *
 * A modern flamegraph visualization with:
 * - Orange/red color scheme
 * - Root-at-bottom orientation
 * - Interactive zooming
 * - Text truncation toggle
 * - Diff mode support
 */

import * as React from 'react';
import {
    ProfileNode,
    PositionedRect,
    LayoutConfig,
    calculateLayout,
    formatNodeName,
    getNodeColor,
    FLAMEGRAPH_COLORS,
    DIFF_COLORS
} from '../utils/flamegraphLayout.ts';
import { formatMetricValue as formatMetricValueUtil } from '../utils/metricUtils.ts';
import './FlameGraphCustom.css';

export interface FlameGraphCustomProps {
    data: ProfileNode;
    width: number;
    height: number;
    onChange?: (node: ProfileNode) => void;
    focusedNode?: ProfileNode;
    showMethodFirst?: boolean;
    diffData?: DiffData;  // Optional diff mode data
    theme?: 'azure' | 'minimal' | 'highcontrast' | 'grayscale';
    metric?: number;  // Metric ID (0-5 = commit metrics, 6-7 = API metrics)
    metricContext?: 'timeline' | 'commit' | 'api';  // Context for unit formatting
    rootAtTop?: boolean;  // If true, render root at top (default: false = root at bottom)
    fontSize?: number;  // Font size for node labels in pixels (default: 11)
}

export interface DiffData {
    mode: 'overlay' | 'differential';
    baseline: ProfileNode;
    comparison: ProfileNode;
    diffNodes: Map<string, DiffNode>;  // Kept for backwards compatibility
    mergedTree?: ProfileNode;  // Merged tree for diff visualization (includes new and removed nodes)
    newNodes?: string[];
    removedNodes?: string[];
    totalDelta?: number;
    totalPercentChange?: number;
}

export interface DiffNode {
    baselineValue: number;
    comparisonValue: number;
    delta: number;
    percentChange: number;
    diffType: 'increased' | 'decreased' | 'unchanged' | 'new' | 'removed';
}

export const FlameGraphCustom: React.FC<FlameGraphCustomProps> = ({
    data,
    width,
    height,
    onChange,
    focusedNode: externalFocusedNode,
    showMethodFirst = false,
    diffData,
    theme = 'azure',
    metric,
    metricContext,
    rootAtTop = false,
    fontSize = 11
}) => {
    const [hoveredNode, setHoveredNode] = React.useState<ProfileNode | null>(null);
    const [tooltipPos, setTooltipPos] = React.useState<{ x: number; y: number } | null>(null);
    const [internalFocusedNode, setInternalFocusedNode] = React.useState<ProfileNode | undefined>(undefined);
    const svgRef = React.useRef<SVGSVGElement>(null);

    // Use external focusedNode if provided, otherwise use internal state
    const focusedNode = externalFocusedNode !== undefined ? externalFocusedNode : internalFocusedNode;

    // Format value based on metric type and context
    // Note: We calculate the unfilteredTotal from the root node's value
    const unfilteredTotal = data?.value;

    const formatValue = React.useCallback((value: number): string => {
        // Use the centralized formatting utility
        // For FlameGraphCustom, we pass the root node's value as unfilteredTotal
        return formatMetricValueUtil(value, metric || 0, metricContext, unfilteredTotal);
    }, [metric, metricContext, unfilteredTotal]);

    const config: LayoutConfig = React.useMemo(() => ({
        containerWidth: width,
        containerHeight: height,
        rowHeight: 20,
        minNodeWidth: 12
    }), [width, height]);

    // Build an id -> node map once per tree so zoom (focusedNode) lookups are O(1)
    // instead of a recursive tree walk per click.
    const nodesById: Map<number, ProfileNode> = React.useMemo(() => {
        const map = new Map<number, ProfileNode>();
        if (!data) return map;
        const stack: ProfileNode[] = [data];
        while (stack.length > 0) {
            const n = stack.pop()!;
            map.set(n.id, n);
            if (n.children) {
                for (const c of n.children) stack.push(c);
            }
        }
        return map;
    }, [data]);

    // Calculate layout — all nodes, then filter to only those wide enough to render.
    // Filtering here (once, memoized) means the render loop only touches visible nodes
    // rather than iterating all ~40k positions and returning null for most of them.
    const positions: PositionedRect[] = React.useMemo(() => {
        if (!data) return [];

        const displayData = focusedNode
            ? (nodesById.get(focusedNode.id) || data)
            : data;

        const allPositions = calculateLayout(displayData, config);

        const visible = allPositions.filter(pos => pos.width >= config.minNodeWidth);

        if (rootAtTop) {
            return visible.map(pos => ({
                ...pos,
                y: height - pos.y - pos.height
            }));
        }

        return visible;
    }, [data, focusedNode, config, rootAtTop, height, nodesById]);

    // Dual scaling for differential mode: use both root delta and max absolute delta
    const deltaScaling = React.useMemo(() => {
        if (!diffData || positions.length === 0) return 0;

        // Get root delta (the displayed root - could be zoomed)
        const rootNode = positions[0]?.node;
        const rootDelta = Math.abs(rootNode?.diffData?.delta || 0);

        // Get max absolute delta across all visible nodes
        const maxAbsDelta = Math.max(...positions.map(p => Math.abs(p.node.diffData?.delta || 0)));

        // Use whichever is larger to ensure colors are meaningful
        // - If root delta is large → colors show contribution to root
        // - If individual changes are large but cancel out → colors still show them
        return Math.max(rootDelta, maxAbsDelta);
    }, [positions, diffData]);

    // Handle node click
    const handleNodeClick = React.useCallback((node: ProfileNode, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent SVG background click
        if (onChange) {
            // Controlled mode: notify parent
            onChange(node);
        } else {
            // Uncontrolled mode: manage internally
            setInternalFocusedNode(node);
        }
    }, [onChange]);

    // Handle background click to reset zoom
    const handleBackgroundClick = React.useCallback(() => {
        if (focusedNode) {
            if (onChange) {
                // Controlled mode: notify parent to reset
                onChange(data);
            } else {
                // Uncontrolled mode: reset internally
                setInternalFocusedNode(undefined);
            }
        }
    }, [onChange, focusedNode, data]);

    // Handle mouse enter
    const handleMouseEnter = React.useCallback((node: ProfileNode, event: React.MouseEvent) => {
        setHoveredNode(node);

        if (svgRef.current) {
            const rect = svgRef.current.getBoundingClientRect();
            setTooltipPos({
                x: event.clientX - rect.left,
                y: event.clientY - rect.top
            });
        }
    }, []);

    // Handle mouse leave
    const handleMouseLeave = React.useCallback(() => {
        setHoveredNode(null);
        setTooltipPos(null);
    }, []);

    // Get theme colors and styling
    const getThemeColors = React.useCallback(() => {
        switch (theme) {
            case 'azure':
                return { fill: '#E87722', stroke: '#000000', strokeWidth: 1, fontWeight: 'bold' as const };
            case 'minimal':
                return { fill: 'transparent', stroke: '#666666', strokeWidth: 1, fontWeight: 'normal' as const };
            case 'highcontrast':
                return { fill: '#FFFFFF', stroke: '#000000', strokeWidth: 2, fontWeight: 'bold' as const };
            case 'grayscale':
                return { fill: '#CCCCCC', stroke: '#666666', strokeWidth: 1, fontWeight: 'normal' as const };
            default:
                return { fill: '#E87722', stroke: '#000000', strokeWidth: 1, fontWeight: 'bold' as const };
        }
    }, [theme]);

    const themeColors = getThemeColors();

    // Get color for node based on mode
    const getColor = React.useCallback((pos: PositionedRect): string => {
        if (diffData) {
            // Read diff data directly from the node object (no Map lookup!)
            const diffNode = pos.node.diffData;
            if (!diffNode) return DIFF_COLORS.unchanged;

            if (diffData.mode === 'overlay') {
                switch (diffNode.diffType) {
                    case 'increased': {
                        const intensity = Math.min(Math.abs(diffNode.percentChange) / 100, 1);
                        const colorIndex = Math.floor(intensity * (DIFF_COLORS.increased.length - 1));
                        return DIFF_COLORS.increased[colorIndex];
                    }
                    case 'decreased': {
                        const intensity = Math.min(Math.abs(diffNode.percentChange) / 100, 1);
                        const colorIndex = Math.floor(intensity * (DIFF_COLORS.decreased.length - 1));
                        return DIFF_COLORS.decreased[colorIndex];
                    }
                    case 'new':
                        return DIFF_COLORS.new;
                    case 'removed':
                        return DIFF_COLORS.removed;
                    default:
                        return DIFF_COLORS.unchanged;
                }
            } else {
                // Differential mode - use gradient for delta values
                // Scale using dual reference (root delta vs max absolute delta)
                return getNodeColor(Math.abs(diffNode.delta), deltaScaling, FLAMEGRAPH_COLORS);
            }
        }

        // Normal mode - use theme color
        return themeColors.fill;
    }, [diffData, deltaScaling, themeColors]);

    // Get tooltip text
    const getTooltipText = React.useCallback((node: ProfileNode): string => {
        if (diffData) {
            // Read diff data directly from the node object
            const diffNode = node.diffData;

            if (diffNode) {
                return `${node.name}\nBaseline: ${formatValue(diffNode.baselineValue)}\nComparison: ${formatValue(diffNode.comparisonValue)}\nDelta: ${diffNode.delta >= 0 ? '+' : ''}${formatValue(Math.abs(diffNode.delta))} (${diffNode.percentChange.toFixed(1)}%)`;
            }
        }

        // Always format with the correct unit based on metric type
        // Don't use node.tooltip from backend as it may have wrong units
        return `${node.name}\n${formatValue(node.value)}`;
    }, [diffData, formatValue]);

    // Render flame graph rectangles
    return (
        <div className="flamegraph-container" style={{ position: 'relative', width, height }}>
            <svg
                ref={svgRef}
                width={width}
                height={height}
                className="flamegraph-svg"
                onClick={handleBackgroundClick}
                style={{ cursor: focusedNode ? 'pointer' : 'default' }}
            >
                {positions.map((pos, index) => {
                    const displayName = formatNodeName(pos.node.name, showMethodFirst);
                    const color = getColor(pos);

                    return (
                        <g key={`${pos.node.id}-${index}`}>
                            <rect
                                x={pos.x}
                                y={pos.y}
                                width={pos.width}
                                height={pos.height}
                                fill={color}
                                stroke={themeColors.stroke}
                                strokeWidth={themeColors.strokeWidth}
                                style={{
                                    cursor: 'pointer',
                                    opacity: hoveredNode?.id === pos.node.id ? 0.8 : 1
                                }}
                                onClick={(e) => handleNodeClick(pos.node, e)}
                                onMouseEnter={(e) => handleMouseEnter(pos.node, e)}
                                onMouseLeave={handleMouseLeave}
                            />
                            {pos.width > 40 && (
                                <text
                                    x={pos.x + 4}
                                    y={pos.y + fontSize + 3}
                                    fontSize={`${fontSize}px`}
                                    fill="#000000"
                                    fontFamily="'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
                                    fontWeight={themeColors.fontWeight}
                                    pointerEvents="none"
                                    style={{
                                        userSelect: 'none'
                                    }}
                                >
                                    <tspan>
                                        {displayName.length > (pos.width / (fontSize * 0.6))
                                            ? displayName.substring(0, Math.floor(pos.width / (fontSize * 0.6))) + '...'
                                            : displayName}
                                    </tspan>
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>

            {/* Custom tooltip (optional - SVG title works too) */}
            {hoveredNode && tooltipPos && (
                <div
                    className="flamegraph-tooltip"
                    style={{
                        position: 'absolute',
                        left: tooltipPos.x + 10,
                        top: tooltipPos.y + 10,
                        background: 'rgba(0, 0, 0, 0.85)',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        pointerEvents: 'none',
                        whiteSpace: 'pre-line',
                        maxWidth: '400px',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}
                >
                    {getTooltipText(hoveredNode)}
                </div>
            )}

            {/* Hint text */}
            {focusedNode && focusedNode.id !== data.id && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: 8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '12px',
                        color: '#ffffff',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        userSelect: 'none'
                    }}
                >
                    Click background to zoom out
                </div>
            )}
        </div>
    );
};
